
import { useState } from 'react';
import { Link } from 'react-router-dom';

const WEB3FORMS_KEY = 'f94e7451-6f14-4ffd-9df2-96960ed9f943';

export default function PageFooter() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;

    setStatus('submitting');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          name: form.name,
          email: form.email,
          message: form.message,
          subject: `[履歷網站] ${form.name} 的來訊`,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setForm({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  const quickLinks = [
    { label: '關於我', to: '/about' },
    { label: '技能', to: '/skills' },
    { label: '經歷', to: '/experience' },
    { label: '個人專案', to: '/side-projects' },
  ];

  const skillTags = ['Python', 'SQL', 'Tableau', 'Pandas', 'PostgreSQL', 'Power BI'];

  return (
    <footer id="contact-section" className="bg-gradient-to-br from-gray-900 to-gray-800 px-4 md:px-8 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

          {/* Column 1 - Brand */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frances
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              法律背景出身，邁向數據領域的轉職者。<br />
              以嚴謹思維分析資料，用數據驅動決策。
            </p>
            <div className="flex gap-3">
              {[
                { icon: 'ri-github-fill', href: 'https://github.com/francesshen-lawxtech' },
                { icon: 'ri-mail-line', href: 'mailto:francesshen.aiclass@gmail.com' },
              ].map((item, index) => (
                <a key={index} href={item.href} className="w-11 h-11 bg-gradient-to-br from-orange-500 to-rose-500 rounded-full flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer">
                  <i className={`${item.icon} text-white text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h4 className="text-base font-bold text-white mb-6">快速連結</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-sm text-gray-400 hover:text-orange-400 transition-colors cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Skills */}
          <div>
            <h4 className="text-base font-bold text-white mb-6">技能專長</h4>
            <div className="flex flex-wrap gap-2">
              {skillTags.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-gray-700 text-gray-300 text-xs font-semibold rounded-full hover:bg-orange-500 hover:text-white transition-colors duration-300">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Column 4 - Contact Form */}
          <div>
            <h4 className="text-base font-bold text-white mb-6">聯絡我</h4>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-3">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-green-400 text-sm font-semibold">訊息已送出！</p>
                <p className="text-gray-500 text-xs mt-1">我會盡快回覆你</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="你的姓名"
                  required
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all text-sm"
                />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="你的電子郵件"
                  required
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all text-sm"
                />
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="留言內容"
                  required
                  rows={3}
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-2.5 rounded-lg bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all text-sm resize-none"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-300 cursor-pointer disabled:opacity-50 text-sm"
                >
                  {status === 'submitting' ? '送出中...' : '送出訊息'}
                </button>
                {status === 'error' && (
                  <p className="text-red-400 text-xs">送出失敗，請直接寄信至 francesshen.aiclass@gmail.com</p>
                )}
              </form>
            )}
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <p className="text-gray-400 text-sm">© 2025 Frances Shen. 保留所有權利。</p>
          <p className="text-gray-500 text-xs">Built with React + TypeScript + Tailwind CSS</p>
        </div>
      </div>
    </footer>
  );
}
